import * as axios from '../../axios-extended'
import * as t from 'io-ts'
import {
  Query,
  NotificationMethod,
  FinancingRequest,
  PublishedInvoice,
  OrganizationInfo,
} from './types'
import {
  InvoiceWithOrganization,
  InvoiceWithTotal,
  NewOrganizationInvoice,
  NewSupplierInvoice,
} from '../../types'

const apiUrl = '/prosperbill/api'

const convertSort = (s: Exclude<Query['sort'], undefined>) =>
  s[0] === 'payer' ? ['customer', s[1]] : s

export const getInvoiceByOrderID = (
  orderID: number,
  cancelToken?: axios.CancelToken
) =>
  axios.get(`${apiUrl}/invoices/order/${orderID}`, {
    decoder: InvoiceWithOrganization,
    cancelToken,
  })

export const getBill = (id: number, cancelToken?: axios.CancelToken) =>
  axios.get(`${apiUrl}/invoices/invoice/${id}`, {
    decoder: InvoiceWithOrganization,
    cancelToken,
  })

export const getBills = (
  organization: string,
  { page = 0, itemsPerPage = 10, sort = ['id', 'desc'] }: Partial<Query>,
  cancelToken?: axios.CancelToken
) => {
  const sortConverted = convertSort(sort)
  return axios.get(`${apiUrl}/invoices/${organization}`, {
    cancelToken,
    params: {
      page,
      size: itemsPerPage,
      sort: `${sortConverted[0]},${sortConverted[1]}`,
    },
    decoder: t.type({
      data: t.array(InvoiceWithTotal),
      count: t.number,
    }),
  })
}

export const getSupplierBills = (
  organization: string,
  { page = 0, itemsPerPage = 10, sort = ['id', 'desc'] }: Partial<Query>
) => {
  const sortConverted = convertSort(sort)
  return axios.get(`${apiUrl}/invoices/${organization}/supplier`, {
    params: {
      page,
      size: itemsPerPage,
      sort: `${sortConverted[0]},${sortConverted[1]}`,
    },
    decoder: t.type({
      data: t.array(InvoiceWithTotal),
      count: t.number,
    }),
  })
}

export const getRegisteredEmail = (myOrg: string, organization: string) => {
  return axios.get(
    `${apiUrl}/invoices/${myOrg}/registered-email/${organization}`,
    {
      decoder: t.type({
        organization: t.string,
        email: t.string,
      }),
    }
  )
}

export const sendBills = (
  organization: string,
  method: NotificationMethod,
  ids: number[],
  cancelToken?: axios.CancelToken
) =>
  axios.post(`${apiUrl}/invoices/${organization}/${method}/notify`, ids, {
    cancelToken,
    decoder: t.unknown,
  })

export const sendSupplierBills = (
  organization: string,
  ids: number[],
  cancelToken?: axios.CancelToken
) =>
  axios.post(
    `${apiUrl}/invoices/${organization}/notify-supplier-invoices`,
    ids,
    {
      cancelToken,
      decoder: t.unknown,
    }
  )

export const deleteBill = (
  organization: string,
  id: number,
  cancelToken?: axios.CancelToken
) =>
  axios.delete_(`${apiUrl}/invoices/${organization}/${id}`, {
    cancelToken,
    params: { id },
    decoder: t.unknown,
  })

export type NewBill = {
  address: string
  amount: number
  createdAt: Date
  customerId: number
  dueAt: Date
  email: string
  items: {
    item: {
      description: string
      id: number
      name: string
      price: number
    }
    quantity: number
  }[]
  phoneNumber: string
  reference: string
}

export const createOrganizationInvoice = (
  organization: string,
  invoice: NewOrganizationInvoice,
  cancelToken?: axios.CancelToken
) =>
  axios.post(`${apiUrl}/invoices/${organization}`, invoice, {
    cancelToken,
    decoder: t.unknown,
  })

export const createSupplierInvoice = (
  supplier: string,
  invoice: NewSupplierInvoice
) =>
  axios.post(`${apiUrl}/invoices/${supplier}/supplier`, invoice, {
    decoder: t.unknown,
  })

export const updateBill = (
  organization: string,
  id: number,
  bill: NewOrganizationInvoice
) =>
  axios.put(`${apiUrl}/invoices/${organization}/invoice/${id}`, bill, {
    decoder: t.unknown,
  })

export const publishBill = (
  organization: string,
  b: PublishedInvoice,
  cancelToken?: axios.CancelToken
) => {
  console.log('request ', JSON.stringify(b))
  return axios.post(`/smart-hub/1.0/${organization}/invoices`, b, {
    cancelToken,
    decoder: t.unknown,
  })
}

export const getPublishedBills = (
  organization: string,
  cancelToken?: axios.CancelToken
) =>
  axios.get(`/smart-hub/1.0/${organization}/invoices`, {
    cancelToken,
    decoder: t.array(PublishedInvoice),
  })

export const getSmartHubInvoice = (organization: string, invoiceID: number) =>
  axios.get(`/smart-hub/1.0/${organization}/invoices/${invoiceID}`, {
    decoder: PublishedInvoice,
  })

export const getFinancingRequest = (organization: string, requestID: number) =>
  axios.get(`/smart-hub/1.0/${organization}/invoices/${requestID}/request`, {
    decoder: FinancingRequest,
  })

export const unpublishBill = (
  organization: string,
  invoiceID: number,
  cancelToken?: axios.CancelToken
) =>
  axios.delete_(`/smart-hub/1.0/${organization}/invoices/${invoiceID}`, {
    cancelToken,
    params: { invoiceID },
    decoder: t.unknown,
  })

export const invoiceRefExists = (organization: string, ref: string) =>
  axios.get(`/prosperbill/api/invoices/${organization}/invoice-by-ref/${ref}`, {
    decoder: t.unknown,
  })

export const verifyOrganizationNameExists = (
  name: string,
  cancelToken?: axios.CancelToken
) =>
  axios.get(`/uaa/api/organizations/${name}/exists`, {
    cancelToken,
    decoder: t.unknown,
  })

export const getOrganizationPublicInfo = (
  organization: string,
  cancelToken?: axios.CancelToken
) =>
  axios.get(`/uaa/api/organizations/${organization}/public`, {
    cancelToken,
    decoder: OrganizationInfo,
  })
